import { cn, dayjs, getDiffExpirationDate } from '@/utils';
import {
	KeyboardEvent as ReactKeyboardEvent,
	memo,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { DatePicker } from 'antd';
import { DatePickerProps } from 'antd/es/date-picker';

import type { Dayjs } from 'dayjs';

import { DatePickerData, DatePickerFormProps } from '../date-picker-form';
import {
	createAllowedDateResolver,
	relaxDisabledDate,
} from '../date-picker-form/segmented-date.util';

type PanelMode = NonNullable<DatePickerProps['mode']>;

type SegmentKey = 'year' | 'month' | 'day';

type SegmentValues = Record<SegmentKey, string>;

export type DateSegmentPickerFormProps = Omit<
	DatePickerFormProps,
	'onChange' | 'mode' | 'open'
> & {
	onChange?: (data: DatePickerData) => void;
	/**
	 * Class riêng cho từng ô. Container đã `absolute` nên có thể canh chính xác
	 * bằng `absolute left-[x] w-[y]` cho mỗi ô; nếu không truyền thì 3 ô xếp
	 * liền nhau theo flex.
	 *
	 * 각 칸에 적용할 class입니다. 컨테이너가 `absolute`이므로 칸마다
	 * `absolute left-[x] w-[y]`로 정확히 배치할 수 있으며, 지정하지 않으면
	 * 세 칸이 flex로 나란히 배치됩니다.
	 */
	yearClassName?: string;
	monthClassName?: string;
	dayClassName?: string;
	placeholders?: Partial<SegmentValues>;
	/**
	 * Nới vùng bấm của mỗi ô thêm `x` px mỗi bên và `y` px trên/dưới. Phần nới
	 * là padding + margin âm nên CHỮ KHÔNG BỊ XÊ DỊCH, chỉ vùng click to ra.
	 * Truyền `{ x: 0, y: 0 }` nếu 2 ô quá sát nhau và bị chồng vùng bấm.
	 *
	 * 각 칸의 클릭 영역을 좌우 `x`px, 상하 `y`px 넓힙니다. padding과 음수 margin을
	 * 함께 사용하므로 글자 위치는 그대로이고 클릭 영역만 커집니다. 칸 간격이
	 * 좁아 영역이 겹치면 `{ x: 0, y: 0 }`으로 끄면 됩니다.
	 */
	hitArea?: { x?: number; y?: number };
	/**
	 * Chỉ dùng cho chế độ canh theo `format`: `false` để bỏ phần khoảng trắng
	 * đệm giữa các ô (khi tự canh bằng class riêng).
	 *
	 * `format` 기반 배치에서만 사용합니다. 칸 사이의 공백 채움을 없애려면
	 * `false`로 설정합니다.
	 */
	showFormatFiller?: boolean;
	/**
	 * Cờ trong config để chọn component này thay cho `DatePickerForm`,
	 * bản thân component không dùng tới.
	 *
	 * config에서 `DatePickerForm` 대신 이 컴포넌트를 사용하도록 지정하는
	 * 플래그이며 컴포넌트 내부에서는 사용하지 않습니다.
	 */
	segmentedInputs?: boolean;
};

type FormatPart =
	| { type: 'text'; text: string }
	| { type: 'segment'; key: SegmentKey; length: number };

const SEGMENTS: Array<{
	key: SegmentKey;
	mode: PanelMode;
	defaultClassName: string;
}> = [
	{ key: 'year', mode: 'year', defaultClassName: 'w-[34px]' },
	{ key: 'month', mode: 'month', defaultClassName: 'w-[22px]' },
	{ key: 'day', mode: 'date', defaultClassName: 'w-[22px]' },
];

const SEGMENT_MAP = SEGMENTS.reduce(
	(acc, segment) => ({ ...acc, [segment.key]: segment }),
	{} as Record<SegmentKey, (typeof SEGMENTS)[number]>
);

const DEFAULT_PARTS: FormatPart[] = [
	{ type: 'segment', key: 'year', length: 4 },
	{ type: 'segment', key: 'month', length: 2 },
	{ type: 'segment', key: 'day', length: 2 },
];

const EMPTY_SEGMENTS: SegmentValues = { year: '', month: '', day: '' };

// Ô nhập chỉ rộng đúng bằng 2-4 chữ số nên mặc định nới vùng bấm ra 2 bên
// (tối đa bấy nhiêu px, và không bao giờ lấn quá nửa khoảng cách sang ô kế).
//
// 입력 칸은 숫자 2~4자 너비뿐이라 클릭 영역을 좌우로 넓힙니다(최대 px 값이며
// 옆 칸과의 간격 절반을 넘지 않습니다).
const DEFAULT_HIT_AREA = { x: 24, y: 5 };

// `Nch` là bề ngang đúng bằng N chữ số '0', nhưng Pretendard dùng chữ số tỉ lệ
// (số '2', '6'... rộng hơn '0') và Chrome còn chừa chỗ cho con trỏ, nên chữ bị
// cắt mất một chút. Nới thêm vài px rồi bù lại bằng margin âm ở hàm đo vùng bấm
// -> ô rộng hơn nhưng vị trí các phần sau không đổi.
//
// `Nch`는 '0' N개의 너비인데 Pretendard는 비례 숫자를 쓰고(‘2’, ‘6’ 등이 '0'보다
// 넓음) 크롬이 커서 자리도 남겨 두어 글자가 잘립니다. 몇 px 넓힌 뒤 클릭 영역
// 계산에서 음수 margin으로 상쇄해 뒤쪽 위치는 그대로 유지합니다.
const SEGMENT_EXTRA_WIDTH = 5;

/**
 * Tách `format` thành các ô nhập + phần đệm ở giữa. Nhờ vậy layout giữ nguyên
 * cách canh chữ của `format` cũ (khoảng trắng canh theo chữ 년/월/일 in sẵn
 * trên phôi), chỉ khác là mỗi token trở thành 1 ô nhập riêng.
 *
 * `format`을 입력 칸과 사이 채움 문자로 분리합니다. 기존 `format`의 공백 정렬
 * (양식에 인쇄된 년/월/일 위치)을 그대로 유지하면서 각 토큰만 개별 입력 칸이
 * 됩니다.
 */
const parseFormat = (format?: string): FormatPart[] => {
	if (!format) return DEFAULT_PARTS;

	const parts: FormatPart[] = [];
	const tokenRegex = /YYYY|YY|MM|DD/g;

	let lastIndex = 0;
	let matched: RegExpExecArray | null;

	// eslint-disable-next-line no-cond-assign
	while ((matched = tokenRegex.exec(format))) {
		if (matched.index > lastIndex) {
			parts.push({
				type: 'text',
				text: format.slice(lastIndex, matched.index),
			});
		}

		const token = matched[0];

		parts.push({
			type: 'segment',
			key: token.startsWith('Y')
				? 'year'
				: token.startsWith('M')
					? 'month'
					: 'day',
			length: token.length,
		});

		lastIndex = matched.index + token.length;
	}

	if (lastIndex < format.length) {
		parts.push({ type: 'text', text: format.slice(lastIndex) });
	}

	const hasAllSegments = SEGMENTS.every(({ key }) =>
		parts.some((part) => part.type === 'segment' && part.key === key)
	);

	return hasAllSegments ? parts : DEFAULT_PARTS;
};

const toSegmentValues = (
	date: Dayjs | null | undefined,
	yearLength: number
): SegmentValues =>
	date?.isValid()
		? {
				year: date.format(yearLength === 2 ? 'YY' : 'YYYY'),
				month: date.format('MM'),
				day: date.format('DD'),
			}
		: EMPTY_SEGMENTS;

const isEmptySegments = (segments: SegmentValues) =>
	SEGMENTS.every(({ key }) => !segments[key]);

/**
 * Ghép 3 ô thành ngày. Ngày vượt quá số ngày của tháng thì kẹp lại
 * (31/04 -> 30/04) thay vì nhảy sang tháng sau.
 *
 * 세 칸을 하나의 날짜로 합칩니다. 해당 월의 마지막 날을 넘는 일자는 다음 달로
 * 넘어가지 않고 마지막 날로 보정합니다(4월 31일 -> 4월 30일).
 */
const toDate = (segments: SegmentValues, yearLength: number): Dayjs | null => {
	const { year, month, day } = segments;

	if (year.length !== yearLength || !month || !day) return null;

	const monthNumber = Number(month);
	const dayNumber = Number(day);

	if (monthNumber < 1 || monthNumber > 12 || dayNumber < 1) return null;

	// Phôi dùng 2 số cuối của năm (YY) -> quy về 20xx.
	//
	// 양식이 연도 두 자리(YY)를 쓰는 경우 20xx로 변환합니다.
	const fullYear = yearLength === 2 ? `20${year}` : year;

	if (!Number(fullYear)) return null;

	const firstDay = dayjs(
		`${fullYear}-${String(monthNumber).padStart(2, '0')}-01`,
		'YYYY-MM-DD'
	);

	if (!firstDay.isValid()) return null;

	return firstDay.date(Math.min(dayNumber, firstDay.daysInMonth()));
};

const DateSegmentPickerForm = ({
	className,
	containerClassName,
	yearClassName,
	monthClassName,
	dayClassName,
	placeholders,
	hitArea,
	showFormatFiller = true,
	boxStyle,
	style,
	value,
	name = '',
	format,
	outputFormat = 'YYYY-MM-DD',
	onChange,
	showDifferenceDate = false,
	disabled,
	disabledDate,
	inputReadOnly = false,
	onPanelChange,
	// Các prop chỉ dành cho DatePickerForm, không dùng ở đây.
	// DatePickerForm 전용 prop이라 여기서는 사용하지 않습니다.
	segmentedInputs: _segmentedInputs,
	segmentedPanel: _segmentedPanel,
	guideTooltip: _guideTooltip,
	guideTooltipProps: _guideTooltipProps,
	inputClassName: _inputClassName,
	...rest
}: DateSegmentPickerFormProps) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const inputRefs = useRef<
		Partial<Record<SegmentKey, HTMLInputElement | null>>
	>({});
	const focusedRef = useRef<SegmentKey | null>(null);
	const openRef = useRef(false);

	const [segments, setSegments] = useState<SegmentValues>(EMPTY_SEGMENTS);
	const [open, setOpen] = useState(false);
	const [mode, setMode] = useState<PanelMode>('date');
	const [pickerValue, setPickerValue] = useState<Dayjs | null>(null);

	const segmentsRef = useRef(segments);
	segmentsRef.current = segments;

	const parts = useMemo(() => parseFormat(format), [format]);

	const segmentLengths = useMemo(
		() =>
			parts.reduce(
				(acc, part) =>
					part.type === 'segment' ? { ...acc, [part.key]: part.length } : acc,
				{} as Record<SegmentKey, number>
			),
		[parts]
	);

	const yearLength = segmentLengths.year;

	const yearLengthRef = useRef(yearLength);
	yearLengthRef.current = yearLength;

	const valueDate = useMemo(() => {
		const date = value ? dayjs(value, outputFormat) : null;

		return date?.isValid() ? date : null;
	}, [value, outputFormat]);

	// Giá trị từ ngoài (data của form) là nguồn chính; chỉ bỏ qua khi user đang
	// gõ dở để không bị "đá" ký tự đang nhập.
	//
	// 폼 데이터가 기준값이며, 사용자가 입력 중일 때만 동기화를 건너뜁니다.
	useEffect(() => {
		if (focusedRef.current) return;

		setSegments(toSegmentValues(valueDate, yearLength));
	}, [valueDate, yearLength]);

	const disabledDateInner = useMemo(
		() => relaxDisabledDate(disabledDate),
		[disabledDate]
	);

	const resolveAllowedDate = useMemo(
		() => createAllowedDateResolver(disabledDateInner as any),
		[disabledDateInner]
	);

	const isDisabledDate = useCallback(
		(date: Dayjs) => !!disabledDate?.(date, { type: 'date' } as any),
		[disabledDate]
	);

	// Ngày dùng để mở panel khi chưa chọn xong: lấy phần user đã gõ, thiếu thì
	// lấy theo hôm nay -> mở lịch đúng năm/tháng đang nhập.
	//
	// 아직 완성되지 않은 상태에서 패널을 열 때 기준이 되는 날짜입니다.
	// 입력된 값을 우선 사용하고 없는 부분은 오늘 기준으로 채웁니다.
	const getPanelBase = useCallback(() => {
		const current = toDate(segmentsRef.current, yearLengthRef.current);

		if (current) return current;

		const today = dayjs();
		const { year, month } = segmentsRef.current;
		const monthNumber = Number(month);
		const hasYear = year.length === yearLengthRef.current;

		return dayjs(
			`${
				hasYear
					? `${yearLengthRef.current === 2 ? '20' : ''}${year}`
					: today.format('YYYY')
			}-${
				monthNumber >= 1 && monthNumber <= 12
					? String(monthNumber).padStart(2, '0')
					: today.format('MM')
			}-01`,
			'YYYY-MM-DD'
		);
	}, []);

	const togglePanel = useCallback((next: boolean) => {
		openRef.current = next;
		setOpen(next);
	}, []);

	const emit = useCallback(
		(date: Dayjs | null) => {
			const nextValue = date ? date.format(outputFormat) : '';

			if (nextValue === (value || '')) return;

			onChange?.({ date, dateString: nextValue, name, outputFormat });
		},
		[onChange, name, outputFormat, value]
	);

	const focusSegment = useCallback((key: SegmentKey) => {
		const input = inputRefs.current[key];

		if (!input) return;

		input.focus({ preventScroll: true });
		input.select();
	}, []);

	const moveFocus = useCallback(
		(key: SegmentKey, step: 1 | -1) => {
			const index = SEGMENTS.findIndex((segment) => segment.key === key);
			const next = SEGMENTS[index + step];

			if (!next) return;

			focusSegment(next.key);
			if (openRef.current) setMode(next.mode);
		},
		[focusSegment]
	);

	const showPanel = useCallback(
		(key: SegmentKey) => {
			if (disabled) return;

			const nextMode = SEGMENT_MAP[key].mode;

			setPickerValue(getPanelBase());
			setMode(nextMode);

			if (openRef.current) return;

			togglePanel(true);
			// antd reset `mode` về `picker` ngay khi mở, nên phải set lại ở tick sau.
			//
			// antd가 열릴 때 `mode`를 `picker`로 되돌리므로 다음 tick에서 다시 지정합니다.
			setTimeout(() => setMode(nextMode), 0);
		},
		[disabled, getPanelBase, togglePanel]
	);

	const commit = useCallback(
		(date: Dayjs) => {
			setSegments(toSegmentValues(date, yearLengthRef.current));
			togglePanel(false);
			emit(date);
		},
		[emit, togglePanel]
	);

	/**
	 * Chốt giá trị khi rời khỏi field: đủ và hợp lệ thì chuẩn hoá (pad số 0),
	 * ngược lại xoá trắng để dữ liệu không bị "nửa vời".
	 *
	 * 필드를 벗어날 때 값을 확정합니다. 유효하면 0을 채워 정규화하고,
	 * 그렇지 않으면 값을 비워 불완전한 데이터가 남지 않게 합니다.
	 */
	const finalize = useCallback(() => {
		const date = toDate(segmentsRef.current, yearLengthRef.current);

		if (date && !isDisabledDate(date)) {
			setSegments(toSegmentValues(date, yearLengthRef.current));
			emit(date);
			return;
		}

		if (!isEmptySegments(segmentsRef.current)) setSegments(EMPTY_SEGMENTS);

		emit(null);
	}, [emit, isDisabledDate]);

	const finalizeRef = useRef(finalize);
	finalizeRef.current = finalize;

	// Panel do component tự đóng (click ra ngoài / ESC / chọn xong) để việc click
	// qua lại giữa 3 ô không làm lịch nhấp nháy.
	//
	// 세 칸을 오갈 때 달력이 깜빡이지 않도록 패널 닫기는 컴포넌트가 직접 처리합니다.
	useEffect(() => {
		if (!open) return;

		const onDocumentMouseDown = (event: MouseEvent) => {
			const target = event.target as HTMLElement | null;

			if (!target) return;
			if (containerRef.current?.contains(target)) return;
			if (target.closest?.('.ant-picker-dropdown')) return;

			togglePanel(false);
			finalizeRef.current();
		};

		document.addEventListener('mousedown', onDocumentMouseDown);

		return () => document.removeEventListener('mousedown', onDocumentMouseDown);
	}, [open, togglePanel]);

	const onChangeSegment = useCallback(
		(key: SegmentKey, raw: string) => {
			const length = segmentLengths[key];
			let digits = raw.replace(/\D/g, '').slice(0, length);

			// Ô tháng gõ '2' thì chỉ có thể là '02', ô ngày gõ '4' chỉ có thể là '04'
			// -> tự thêm số 0 rồi nhảy ô kế tiếp.
			//
			// 월에 '2', 일에 '4'처럼 더 이어질 수 없는 숫자는 0을 붙여 완성하고
			// 다음 칸으로 이동합니다.
			if (
				key !== 'year' &&
				digits.length === 1 &&
				Number(digits) > (key === 'month' ? 1 : 3)
			) {
				digits = `0${digits}`;
			}

			const next = { ...segments, [key]: digits };

			setSegments(next);

			const date = toDate(next, yearLength);

			if (date && !isDisabledDate(date)) emit(date);

			if (digits.length === length) moveFocus(key, 1);
		},
		[segments, segmentLengths, yearLength, emit, isDisabledDate, moveFocus]
	);

	const onKeyDownSegment = useCallback(
		(key: SegmentKey, event: ReactKeyboardEvent<HTMLInputElement>) => {
			const input = event.currentTarget;

			switch (event.key) {
				case 'Escape':
					togglePanel(false);
					break;

				case 'Enter':
					event.preventDefault();
					togglePanel(false);
					finalizeRef.current();
					break;

				case 'Backspace':
					if (!input.value) {
						event.preventDefault();
						moveFocus(key, -1);
					}
					break;

				case 'ArrowLeft':
					if (input.selectionStart === 0) {
						event.preventDefault();
						moveFocus(key, -1);
					}
					break;

				case 'ArrowRight':
					if (input.selectionStart === input.value.length) {
						event.preventDefault();
						moveFocus(key, 1);
					}
					break;

				default:
					break;
			}
		},
		[moveFocus, togglePanel]
	);

	/**
	 * Bấm vào khoảng trống trên dòng (không trúng ô nào) thì đưa về ô gần nhất
	 * theo trục ngang, đỡ phải nhắm đúng ô nhỏ.
	 *
	 * 줄의 빈 공간을 클릭하면 가로로 가장 가까운 칸으로 보냅니다. 작은 칸을
	 * 정확히 겨냥하지 않아도 됩니다.
	 */
	const onClickContainer = useCallback(
		(event: React.MouseEvent<HTMLDivElement>) => {
			if (event.target !== event.currentTarget) return;

			const nearest = SEGMENTS.reduce<{
				key: SegmentKey;
				distance: number;
			} | null>((best, { key }) => {
				const rect = inputRefs.current[key]?.getBoundingClientRect();

				if (!rect) return best;

				const distance =
					event.clientX < rect.left
						? rect.left - event.clientX
						: event.clientX > rect.right
							? event.clientX - rect.right
							: 0;

				return !best || distance < best.distance ? { key, distance } : best;
			}, null);

			if (!nearest) return;

			event.preventDefault();
			focusSegment(nearest.key);
			showPanel(nearest.key);
		},
		[focusSegment, showPanel]
	);

	// Những thứ ảnh hưởng tới vị trí / bề ngang của 3 ô: đổi thì phải đo lại.
	//
	// 세 칸의 위치와 너비에 영향을 주는 값들이며, 바뀌면 다시 측정합니다.
	const layoutKey = [
		format,
		className,
		yearClassName,
		monthClassName,
		dayClassName,
		showFormatFiller,
		JSON.stringify(style || {}),
	].join('|');

	const hitMaxX = hitArea?.x ?? DEFAULT_HIT_AREA.x;
	const hitMaxY = hitArea?.y ?? DEFAULT_HIT_AREA.y;

	// Mỗi ô chỉ rộng bằng đúng 2-4 chữ số nên rất khó bấm trúng. Ở đây nới vùng
	// bấm của từng ô ra tới nửa khoảng cách với ô bên cạnh bằng padding + margin
	// âm: `content-box` giữ nguyên `width` phần chữ, padding phình ra ngoài rồi
	// margin âm kéo lại -> CHỮ VÀ KHOẢNG CÁCH GIỮA CÁC Ô KHÔNG ĐỔI.
	// Đo sau khi đã reset padding/margin nên kết quả không phụ thuộc lần chạy
	// trước (không lặp vô hạn).
	//
	// 각 칸이 숫자 2~4자 너비뿐이라 클릭이 어렵습니다. padding과 음수 margin으로
	// 옆 칸과의 간격 절반까지 클릭 영역을 넓히되, `content-box`로 글자 너비는
	// 그대로 두고 padding만큼 음수 margin을 주므로 글자 위치와 칸 간격은 변하지
	// 않습니다. 측정 전에 padding/margin을 초기화하므로 이전 실행 결과에
	// 영향받지 않습니다.
	useLayoutEffect(() => {
		const applyHitArea = () => {
			const inputs = SEGMENTS.map(({ key }) => inputRefs.current[key]).filter(
				Boolean
			) as HTMLInputElement[];

			if (!inputs.length) return;

			// Ô canh theo `format` đã được cộng sẵn phần nới vào `width` (React
			// style) nên chỉ cần bù margin phải; ô tự canh bằng class thì cộng phần
			// nới ngay tại đây và dịch trái nửa phần nới để chữ vẫn nằm giữa như cũ.
			//
			// `format` 기반 칸은 React style의 `width`에 이미 여유가 포함되어 있어
			// 오른쪽 margin만 상쇄하면 되고, class로 직접 배치한 칸은 여기서 여유를
			// 더한 뒤 절반만큼 왼쪽으로 밀어 글자가 기존과 같은 자리에 오게 합니다.
			const isAutoWidth = (input: HTMLInputElement) =>
				input.dataset.autoWidth === '1';

			inputs.forEach((input) => {
				input.style.boxSizing = 'content-box';
				input.style.padding = '0';

				if (isAutoWidth(input)) {
					input.style.margin = `0 ${-SEGMENT_EXTRA_WIDTH}px 0 0`;
				} else {
					input.style.width = '';
					input.style.margin = '0';
				}
			});

			const boxes = inputs
				.map((input) => ({ input, rect: input.getBoundingClientRect() }))
				.sort((a, b) => a.rect.left - b.rect.left);

			boxes.forEach(({ input, rect }, index) => {
				const auto = isAutoWidth(input);

				if (!auto) input.style.width = `${rect.width + SEGMENT_EXTRA_WIDTH}px`;

				if (!hitMaxX && !hitMaxY) {
					if (!auto) input.style.margin = `0 0 0 ${-SEGMENT_EXTRA_WIDTH / 2}px`;
					return;
				}

				const previous = boxes[index - 1]?.rect;
				const next = boxes[index + 1]?.rect;

				const left = previous
					? Math.max(0, Math.min(hitMaxX, (rect.left - previous.right) / 2))
					: hitMaxX;
				const right = next
					? Math.max(0, Math.min(hitMaxX, (next.left - rect.right) / 2))
					: hitMaxX;

				const extraLeft = auto ? 0 : SEGMENT_EXTRA_WIDTH / 2;
				const extraRight = auto ? SEGMENT_EXTRA_WIDTH : 0;

				input.style.padding = `${hitMaxY}px ${right}px ${hitMaxY}px ${left}px`;
				input.style.margin = `${-hitMaxY}px ${-right - extraRight}px ${-hitMaxY}px ${-left - extraLeft}px`;
			});
		};

		applyHitArea();

		// Font web load xong thì bề ngang chữ đổi -> đo lại 1 lần.
		//
		// 웹폰트가 로드되면 글자 너비가 달라지므로 한 번 더 측정합니다.
		let cancelled = false;

		document.fonts?.ready.then(() => {
			if (!cancelled) applyHitArea();
		});

		return () => {
			cancelled = true;
		};
	}, [layoutKey, hitMaxX, hitMaxY]);

	const onBlurSegment = useCallback(() => {
		focusedRef.current = null;

		// Đợi 1 tick để biết focus đã đi đâu (ô khác / lịch / ra ngoài).
		//
		// 포커스가 어디로 이동했는지 확인하기 위해 한 tick 기다립니다.
		setTimeout(() => {
			if (containerRef.current?.contains(document.activeElement)) return;
			if (openRef.current) return;

			finalizeRef.current();
		}, 0);
	}, []);

	/**
	 * User vừa click 1 ô năm (year -> month) hoặc 1 ô tháng (month -> date).
	 * Nếu ghép vào là đủ ngày thì chốt luôn, chưa đủ thì điền phần vừa chọn rồi
	 * cho panel chạy tiếp xuống cấp nhỏ hơn.
	 *
	 * 사용자가 연도 셀(year -> month) 또는 월 셀(month -> date)을 클릭한 경우입니다.
	 * 합쳐서 날짜가 완성되면 바로 확정하고, 아니면 선택한 부분만 채운 뒤
	 * 다음 단계 패널로 내려갑니다.
	 */
	const onPanelChangeInner = useCallback(
		(panelDate: any, nextMode: PanelMode) => {
			onPanelChange?.(panelDate, nextMode);

			const unit: SegmentKey | null =
				mode === 'year' && nextMode === 'month'
					? 'year'
					: mode === 'month' && nextMode === 'date'
						? 'month'
						: null;

			if (!unit) {
				setMode(nextMode);
				return;
			}

			const next = {
				...segmentsRef.current,
				[unit]: panelDate.format(
					unit === 'year' ? (yearLength === 2 ? 'YY' : 'YYYY') : 'MM'
				),
			};
			const merged = toDate(next, yearLength);
			const allowed = merged ? resolveAllowedDate(merged) : null;

			if (allowed) {
				commit(allowed);
				return;
			}

			setSegments(next);
			setPickerValue(panelDate);
			setMode(nextMode);
			focusSegment(unit === 'year' ? 'month' : 'day');
		},
		[mode, yearLength, onPanelChange, resolveAllowedDate, commit, focusSegment]
	);

	const onSelectDate = useCallback(
		(date: Dayjs | null) => {
			if (!date) return;

			commit(date);
			focusSegment('day');
		},
		[commit, focusSegment]
	);

	const diffDate = useMemo(() => getDiffExpirationDate(value), [value]);

	const showDiffDate = showDifferenceDate && diffDate > 0;

	const segmentClassNames: Record<SegmentKey, string | undefined> = {
		year: yearClassName,
		month: monthClassName,
		day: dayClassName,
	};

	// Có class riêng cho từng ô -> tự canh vị trí, bỏ phần đệm của `format`.
	//
	// 칸별 class를 지정하면 직접 배치하는 것이므로 `format`의 채움 문자는 생략합니다.
	const hasCustomLayout = !!(yearClassName || monthClassName || dayClassName);
	const showFiller = showFormatFiller && !hasCustomLayout;

	// `width` trong `style` là bề ngang của cả field (config cũ) nên không áp cho
	// từng ô; các thuộc tính chữ (fontSize...) thì phải áp cho cả ô lẫn phần đệm
	// để 2 bên cùng metric.
	//
	// `style`의 `width`는 필드 전체 너비(기존 config)라서 개별 칸에는 적용하지
	// 않습니다. 글자 관련 속성은 칸과 채움 문자 모두에 적용해 동일한 metric을
	// 유지합니다.
	const { width: _width, ...textStyle } = style || {};

	return (
		<div
			ref={containerRef}
			className={cn('flex cursor-text items-center', containerClassName)}
			style={boxStyle}
			onMouseDown={onClickContainer}
		>
			{showDiffDate && (
				<div className="text-[12px] leading-normal">{`[D-${diffDate}]`}</div>
			)}

			{parts.map((part, index) => {
				if (part.type === 'text') {
					return showFiller ? (
						<span
							key={`text-${index}`}
							// Phần đệm không nhận click để nhường vùng bấm cho các ô.
							//
							// 채움 문자는 칸의 클릭 영역을 가리지 않도록 클릭을 받지 않습니다.
							className={cn(
								'pointer-events-none whitespace-pre text-[12px] font-light text-font2',
								className
							)}
							style={textStyle}
						>
							{part.text}
						</span>
					) : null;
				}

				const segmentClassName = segmentClassNames[part.key];

				return (
					<input
						key={part.key}
						ref={(element) => (inputRefs.current[part.key] = element)}
						className={cn(
							'h-[19px] border-none bg-transparent p-0 text-center text-[12px] font-light text-font2 outline-none',
							segmentClassName ||
								(showFiller ? '' : SEGMENT_MAP[part.key].defaultClassName),
							className
						)}
						style={{
							// Rộng đúng bằng số ký tự của token để giữ nguyên vị trí chữ
							// như khi còn dùng 1 input với `format`.
							//
							// 토큰 글자 수만큼 너비를 잡아 기존 단일 input + `format`
							// 배치와 동일한 위치를 유지합니다.
							...(showFiller && !segmentClassName
								? {
										width: `calc(${part.length}ch + ${SEGMENT_EXTRA_WIDTH}px)`,
										// Chữ bám mép trái ô = đúng vị trí token trong `format`
										// (phần nới thêm nằm hết bên phải).
										//
										// 글자를 칸 왼쪽에 붙여 `format`의 토큰 위치와 정확히
										// 맞춥니다(넓힌 부분은 모두 오른쪽).
										textAlign: 'left' as const,
									}
								: {}),
							...textStyle,
						}}
						data-auto-width={showFiller && !segmentClassName ? '1' : '0'}
						value={segments[part.key]}
						placeholder={placeholders?.[part.key]}
						disabled={disabled}
						readOnly={inputReadOnly}
						inputMode="numeric"
						autoComplete="off"
						maxLength={part.length}
						onChange={(event) => onChangeSegment(part.key, event.target.value)}
						onKeyDown={(event) => onKeyDownSegment(part.key, event)}
						onFocus={() => (focusedRef.current = part.key)}
						onBlur={onBlurSegment}
						onClick={() => showPanel(part.key)}
					/>
				);
			})}

			{/*
				DatePicker chỉ đóng vai trò "neo" cho panel lịch: input gốc bị thu về
				0px, mọi thao tác nhập liệu do 3 ô ở trên đảm nhiệm.

				DatePicker는 달력 패널의 위치 기준 역할만 하며 원래 입력창은 0px로
				숨깁니다. 실제 입력은 위의 세 칸이 담당합니다.
			*/}
			<div className="pointer-events-none absolute bottom-0 left-0 h-0 w-0 overflow-hidden opacity-0">
				<DatePicker
					{...rest}
					open={open}
					mode={mode}
					value={valueDate}
					pickerValue={pickerValue || undefined}
					onPickerValueChange={setPickerValue}
					onPanelChange={onPanelChangeInner}
					onChange={onSelectDate}
					disabledDate={disabledDateInner}
					disabled={disabled}
					allowClear={false}
					suffixIcon={null}
					inputReadOnly
					tabIndex={-1}
					className="!h-0 !w-0 !border-none !p-0 !shadow-none"
				/>
			</div>
		</div>
	);
};

export default memo(DateSegmentPickerForm);
